import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { ServicesBuilder } from '../utils/setup';
import { IItem, Item, ItemService, ICategoryService, AppMiscService, IItemService, ICategory, CategoryService, IProductService, ProductService, ProductType, ICoreItem, PriceType, IInvoice, IInvoiceLine } from '@posx/core';
import { Subscription } from 'dexie';
import { useCategoryStore } from './category';
import { useConfigStore } from './config';
import keyBy from 'lodash/keyBy';
import { logLiveQueryError, logError, logInfo, serializeError, isLiveQueryCursorError } from '@/utils/logger';
import { preciseRound } from '@/utils';

const RESET_STOCK = 10000;

// Reset if stock low/sold-out: global mode excludes opt-out items, legacy mode requires opt-in flag
const needsReset = (i: ICoreItem, globalEnabled?: boolean) =>
  (globalEnabled ? !i.exclude_auto_reset_sold_out : !!i.auto_reset_sold_out) && ((i.stock || 0) < RESET_STOCK || i.is_sold_out);

// Get price for specific tag; percent is a 0-1 ratio (0.9 = 90% of price) based on pre-tier price since item.price is overwritten once a tier is applied
export function getPriceByPlu(item: ICoreItem, plu: string): number {
  const pluPrice = item.prices?.find(price => price.lookup_keys?.includes(plu));
  if (!pluPrice) return item.price;
  if (pluPrice.type === PriceType.Percent) return preciseRound((item.meta?.local_original_price ?? item.price) * (pluPrice.percent ?? 1), 2);
  return pluPrice.amount;
}

// Check if item has specific tag price
export function PriceExistsByPlu(item: ICoreItem, plu: string): boolean {
  return item.prices?.find(price => price.lookup_keys?.includes(plu)) !== undefined;
}

// Promo units allowed per order (promo_max_quantity, default 1); 0 = not a promo item (promo_price unset)
export const getPromoMaxQty = (item?: ICoreItem) => item?.config?.promo_price == null ? 0 : Number(item.config.promo_max_quantity) || 1;
// Promo lines are recognised by price == item.config.promo_price, so lines from any source (e.g. QR ordering) count
export const isPromoLine = (line: IInvoiceLine) => line.item?.config?.promo_price != null && line.price === Number(line.item.config.promo_price);
// Promo units already on the order for this product
export const getPromoQty = (invoice: IInvoice | undefined, uid: string) => (invoice?.lines || []).reduce((sum, l) => sum + (l.item_uid === uid && isPromoLine(l) ? l.quantity : 0), 0);
// Non-promo items skip the line scan, so menu buttons stay off line reactivity; one promo product per order, others fall back to regular price
export const isPromoAvailable = (item: ICoreItem, invoice?: IInvoice) => item.config?.promo_price != null && getPromoQty(invoice, item.uid) < getPromoMaxQty(item) && !invoice?.lines?.some(l => isPromoLine(l) && l.item_uid !== item.uid);
// Shared by the menu button and add-to-order so the shown price is the added price: promo price while quota remains, else the current tier price
export const getOrderPrice = (item: ICoreItem, invoice?: IInvoice) => isPromoAvailable(item, invoice) ? Number(item.config.promo_price) : getPriceByPlu(item, invoice?.price_lookup_key ?? '');
// Max quantity of a promo line: its own units plus the order's remaining promo quota; other lines are unlimited
export const getPromoLineMax = (invoice: IInvoice | undefined, line: IInvoiceLine) => isPromoLine(line) ? line.quantity + Math.max(0, getPromoMaxQty(line.item) - getPromoQty(invoice, line.item_uid)) : Infinity;
// A promo line copy must still fit the order's promo quota
export const isPromoCopyBlocked = (invoice: IInvoice | undefined, line: IInvoiceLine) => isPromoLine(line) && getPromoQty(invoice, line.item_uid) + line.quantity > getPromoMaxQty(line.item);

// Unit conversion: internal ¥/g ↔ display ¥/kg
export const gramToKg = (price: number) => price > 0 ? preciseRound(price * 1000, 2) : price;
export const kgToGram = (price: number) => price > 0 ? preciseRound(price / 1000, 5) : price;

// Get display price for UI (/kg for by_weight, /g for others); with an order it is the price an add would use
export function getDisplayPrice(item: IItem, invoice?: IInvoice): number {
  const price = invoice ? getOrderPrice(item, invoice) : item.price;
  if (item.type === ProductType.ByWeight) return gramToKg(price);
  return price;
}

export const useProductStore = defineStore('product', {
  state: () => ({
    items: [] as IItem[],
    page: 1,
    totalPage: 0,
    filtersValues: {},
    itemService: null as IItemService | null,
    categoryService: null as ICategoryService | null,
    miscService: null as AppMiscService | null,
    productService: null as IProductService | null,
    _subscription: null as Subscription | null,
  }),

  getters: {
    sortedList: (state) => [...state.items].sort((a, b) => a.sequence - b.sequence)
  },

  actions: {
    async startItemLiveQuery() {
      if (this._subscription) return
      const service = await this.getItemService()
      const notDeleted = (item: IItem) => item.deleted_at === undefined || item.deleted_at === null
      const sub: Subscription = service.liveQuery(() => service.table.filter(notDeleted).toArray()).subscribe({
        next: (result) => {
          // markRaw each item: entities are structurally stable between rebuilds, skip deep proxy overhead
          this.items = (result || []).map((i: IItem) => markRaw(i)).sort((a: IItem, b: IItem) => a.sequence - b.sequence)
          this.totalPage = result?.length || 0
        },
        // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
        error: (err) => { logLiveQueryError(err, { store: 'product' }); if (isLiveQueryCursorError(err) && this._subscription === sub) { sub.unsubscribe(); this._subscription = null; } }
      })
      this._subscription = markRaw(sub)
    },
    stopItemLiveQuery() { this._subscription?.unsubscribe(); this._subscription = null; },
    async getItemService() {
      if (!this.itemService) {
        // service singleton is method-only, skip deep proxy
        this.itemService = markRaw(await ServicesBuilder.getInstance().getService<IItemService>(ItemService));
      }
      return this.itemService;
    },

    // Active items by barcode; excludes deleted and delisted so duplicates never shadow a live product
    async findByBarcode(code: string): Promise<IItem[]> {
      if (!code) return [];
      const service = await this.getItemService();
      return service.table.where('barcode').equals(code).filter(i => !i.deleted_at && !i.is_delisted).toArray();
    },
    // First active item whose PLU (zero-padded to 5) matches a scale label
    async findByPlu(plu: string): Promise<IItem[]> {
      const service = await this.getItemService();
      return service.table.filter(i => !i.deleted_at && !i.is_delisted && i.plu?.padStart(5, '0') === plu).limit(1).toArray();
    },

    // Persist a product from the edit dialog; by_weight price is edited per kg but stored per gram
    async saveItem(item: Item) {
      if (item.type === ProductType.ByWeight) item.price = kgToGram(item.price);
      const service = await this.getItemService();
      if (item.id_in_server) await service.updateOne(item.id_in_server, item);
      else await service.addOne(item);
    },

    async getCategoryService(): Promise<ICategoryService> {
      if (!this.categoryService) {
        // service singleton is method-only, skip deep proxy
        this.categoryService = markRaw(await ServicesBuilder.getInstance().getService<ICategoryService>(CategoryService));
      }
      return this.categoryService;
    },

    async getMiscService(): Promise<AppMiscService> {
      if (!this.miscService) {
        // service singleton is method-only, skip deep proxy
        this.miscService = markRaw(await ServicesBuilder.getInstance().getService<AppMiscService>(AppMiscService));
      }
      return this.miscService;
    },

    setFilterValue(values: Partial<Item>) {
      this.filtersValues = values;
    },

    async getList(query?: Partial<Item>) {
      const service = await this.getItemService();
      const result = await service?.getMany(query || this.filtersValues, {
        page: this.page,
      });
      this.items = (result?.list || []).map(markRaw).sort((a, b) => a.sequence - b.sequence);
      this.totalPage = result?.count || 0;
      return this.items;
    },

    async getAll(query?: Partial<Item>) {
      const service = await this.getItemService();
      const result = await service.getMany(query || this.filtersValues, {
        page: 1,
        pageSize: 2000
      });
      this.items = (result?.list || []).map(markRaw).sort((a, b) => a.sequence - b.sequence);
      this.totalPage = result?.count || 0;
      return this.items;
    },

    async changeItemSequence(item: IItem, targetIndex: number) {
      const service = await this.getItemService();
      const miscService = await this.getMiscService();
      const categoryStore = useCategoryStore();
      const hideBarcoded = !!useConfigStore().appConfig?.config?.product?.hide_barcoded_items;
      // normalize deleted_at to null for core's strict === null check; exclude barcode-hidden items so targetIndex matches the visible grid Menu.vue dragged from
      const categoryItems = this.items.filter(i => i.category_uid === item.category_uid && !i.is_delisted && (!hideBarcoded || !i.barcode?.trim())).map(i => (i.deleted_at ??= null, i)).sort((a, b) => a.sequence - b.sequence);
      const originalSequences = new Map(categoryItems.map(i => [i.uid, i.sequence]));
      const categorySequence = categoryStore.categoryMap[item.category_uid]?.sequence || 0;
      const reorderedItems = await miscService.changeSequence(categoryItems, item, targetIndex);
      reorderedItems.forEach(i => {i.sequence = categorySequence * 10000 + i.sequence;});
      const changedItems = reorderedItems.filter(i => originalSequences.get(i.uid) !== i.sequence);
      if (changedItems.length > 0) await service.updateMany(changedItems);
      return reorderedItems;
    },

    async changeCategorySequence(category: ICategory, targetIndex: number) {
      const categoryService = await this.getCategoryService();
      const itemService = await this.getItemService();
      const miscService = await this.getMiscService();
      const categoryStore = useCategoryStore();
      // normalize deleted_at to null for core's strict === null check
      const categories = categoryStore.categories.filter(c => !c.is_delisted).map(c => (c.deleted_at ??= null, c)).sort((a, b) => a.sequence - b.sequence);
      const changedCategories = await miscService.changeSequence(categories, category, targetIndex);
      if (changedCategories.length > 0) {
        await categoryService.updateMany(changedCategories);
        const changedCategoryMap = keyBy(changedCategories, 'uid');
        const itemsToUpdate = this.items.filter((i: IItem) => changedCategoryMap[i.category_uid]);
        itemsToUpdate.forEach((item: IItem) => {
          item.sequence = changedCategoryMap[item.category_uid].sequence * 10000 + (item.sequence % 10000);
        });
        if (itemsToUpdate.length > 0) await itemService.updateMany(itemsToUpdate);
      }
      return categories;
    },

    // Refill items/modifiers to RESET_STOCK on shift open; respects global/per-item settings
    async resetAutoSoldOutItems(userUid: string) {
      try {
        const globalEnabled = useConfigStore().appConfig?.config?.inventory?.sold_out_for_today_only;
        const check = (i: ICoreItem) => needsReset(i, globalEnabled);
        const service = await this.getItemService();
        const categoryService = await this.getCategoryService();
        // one-off snapshot: the store's liveQuery state is empty on screens that never started it
        const liveCategories = new Set((await categoryService.table.filter(c => c.deleted_at == null).toArray()).map(c => c.uid));
        const items: IItem[] = await service.table.filter(i => i.deleted_at == null && liveCategories.has(i.category_uid)).toArray();
        const flagged = items.filter(i => check(i) || i.modifiers?.some(check)).length;
        logInfo('Auto sold-out reset start', { userUid, itemCount: items.length, flagged, globalEnabled });
        const updates: IItem[] = [];
        let modifierCount = 0;
        for (const item of items) {
          const resetSelf = check(item);
          if (!resetSelf && !item.modifiers?.some(check)) continue;
          const copy: IItem = { ...item, modifiers: item.modifiers?.map(m => ({ ...m })) };
          if (resetSelf) { copy.is_sold_out = false; copy.stock = RESET_STOCK; }
          for (const mod of copy.modifiers?.filter(check) || []) {
            mod.is_sold_out = false; mod.stock = RESET_STOCK; modifierCount++;
          }
          updates.push(copy);
        }
        if (!updates.length) return logInfo('Auto sold-out reset skipped', { reason: 'every flagged item is already stocked' });
        await service.updateMany(updates);
        logInfo('Auto sold-out reset done', { items: updates.length, modifiers: modifierCount, updated: updates.length });
      } catch (e) { logError('Reset auto sold-out items failed', serializeError(e)); }
    },
  }
});
